import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import FeatherIcon from "feather-icons-react";
import { useTranslation } from "react-i18next";
import {
  CreateUpdateDraftDataDto,
  FormQuestionDto,
  QuestionComponentType,
} from "@green0meter/api-client";

import { useCreateUpdateDraftData, watchDraftDataQuestionState } from "@api";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
} from "@components/base";
import { useMultistepFormStore } from "@components/common/MultistepForm/MultistepForm.store";
import { FadeInOut } from "@components/motion";
import { useUserStore } from "@store/user";

interface DraftSaveStatus {
  question: FormQuestionDto;
  questionIndentationLevel?: number;
  children?: React.ReactNode;
}

const DraftSaveStatus: FC<DraftSaveStatus> = ({
  question,
  questionIndentationLevel = 0,
  children,
}) => {
  const { t } = useTranslation();
  const { user } = useUserStore();
  const {
    formSettings: {
      formBasics: { id },
    },
  } = useMultistepFormStore();
  const { createUpdateDraftData } = useCreateUpdateDraftData(user, id);
  const [questionSaved, setQuestionSaved] = useState(false);
  const [questionSaveError, setQuestionSaveError] =
    useState<CreateUpdateDraftDataDto | null>(null);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const parentQuestionCode = useMemo(
    () =>
      question.componentType === QuestionComponentType.Table ||
      question.componentType === QuestionComponentType.MultiInput
        ? question.code
        : undefined,
    [question.componentType, question.code],
  );
  const questionCodes = useMemo(
    () =>
      question.componentType === QuestionComponentType.Table ||
      question.componentType === QuestionComponentType.MultiInput
        ? [...(question.childQuestions ?? []).map((q) => q.code)]
        : [question.code],
    [question.componentType, question.childQuestions, question.code],
  );

  const questionDraftState = watchDraftDataQuestionState(
    questionCodes,
    parentQuestionCode,
  );
  const isPending = questionDraftState?.status === "pending";

  const handleTryAgain = (): void => {
    if (questionSaveError) {
      createUpdateDraftData(questionSaveError);
    }
  };

  useEffect(() => {
    if (questionDraftState) {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }

      const olderThan5Seconds = dayjs()
        .subtract(5, "second")
        .isAfter(questionDraftState?.submittedAt);

      // Show "Answer saved" message when the draft save is successful
      if (!olderThan5Seconds && questionDraftState?.status === "success") {
        setQuestionSaved(true);
        setQuestionSaveError(null);

        const newTimeout = setTimeout(() => {
          setQuestionSaved(false);
        }, 3000);

        hideTimeoutRef.current = newTimeout;
      }

      // Hide "Answer saved" & "Error saving answer" message when a new draft save is pending
      if (isPending) {
        setQuestionSaved(false);
        setQuestionSaveError(null);
      }

      // Show "Error saving answer" message when the draft save fails
      if (questionDraftState?.status === "error") {
        setQuestionSaved(false);
        setQuestionSaveError(
          questionDraftState.variables as CreateUpdateDraftDataDto,
        );
      }
    } else {
      setQuestionSaved(false);
      setQuestionSaveError(null);
    }

    // Cleanup on unmount
    return (): void => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
    };
  }, [questionDraftState]);

  return (
    <Card
      color="grey"
      sx={({ palette }) => ({
        transition: "border-color 1s",
        borderColor:
          questionSaved || questionSaveError
            ? questionSaveError
              ? palette.error.main
              : palette.success.main
            : "transparent",
        ml: questionIndentationLevel,
      })}
    >
      <CardContent sx={{ position: "relative" }}>
        {!isPending && (
          <>
            <Box position="absolute" right={16} bottom={16} textAlign="right">
              <FadeInOut active={questionSaved && !questionSaveError}>
                <Typography fontWeight={500} color="success">
                  {t("multistep.answerSaved")}
                </Typography>
              </FadeInOut>
            </Box>
            <Box
              position="absolute"
              right={16}
              bottom={10}
              textAlign="right"
              zIndex={9}
            >
              <FadeInOut active={questionSaveError !== null && !questionSaved}>
                <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                  <Typography fontWeight={500} color="error">
                    {t("multistep.answerSaveError")}
                  </Typography>
                  <Button
                    size="extraSmall"
                    variant="outlined"
                    color="error"
                    startIcon={<FeatherIcon icon="refresh-cw" />}
                    onClick={handleTryAgain}
                  >
                    {t("multistep.tryAgain")}
                  </Button>
                </Stack>
              </FadeInOut>
            </Box>
          </>
        )}
        <Box position="absolute" right={16} bottom={16} textAlign="right">
          {isPending && <CircularProgress size={16} color="success" />}
        </Box>
        {children}
      </CardContent>
    </Card>
  );
};

export default DraftSaveStatus;
